import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { setData } from '@department-of-veterans-affairs/platform-forms-system/actions';
import { focusElement } from '@department-of-veterans-affairs/platform-utilities/ui';

import { mockTestingFlagForAPI } from '../../constants';
import { validateAddress } from '../../utils/api';
import { formatAddress } from '../../utils/helpers';
import { mockAddressValidation } from '../../utils/mockData';
import Loader from '../Loader';

const getAddressString = address => {
  return JSON.stringify({
    city: address.city,
    country: address.countryCodeIso3 || address.country,
    postalCode: address.zipCode || address.postalCode,
    state: address.stateCode || address.state,
    province: address.stateProvince?.code || '',
    street: address.addressLine1 || address.street || '',
    street2: address.addressLine2 || address.street2 || '',
  });
};

const AddressValidationRadio = props => {
  const { formData, setFormData } = props;

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedAddress, setSelectedAddress] = useState('');

  const handleValueChange = (address, id) => {
    setSelectedAddress(id);

    setFormData({
      ...formData,
      addressValidation: getAddressString(address),
    });
  };

  useEffect(() => {
    if (mockTestingFlagForAPI) {
      const raw = mockAddressValidation?.addresses || [];
      setApiData(raw);
      return;
    }

    setLoading(true);

    validateAddress(formData.address)
      .then(res => {
        setApiData(res.addresses);
      })
      .catch(() => {
        handleValueChange(formData.address, 'userEntered');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => {
      if (loading) return;
      if (apiData.length > 0) {
        handleValueChange(apiData[0].address, '0');
        focusElement('h2');
      } else {
        handleValueChange(formData.address, 'userEntered');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiData, loading],
  );

  const renderAddressOption = (
    address,
    showRadio = false,
    id = 'userEntered',
  ) => {
    const { street, cityStateZip } = formatAddress(address);

    if (!showRadio) {
      return (
        <div key={id} className="vads-u-margin-bottom--1p5 dd-privacy-hidden">
          <span className="vads-u-display--block">{street}</span>
          <span className="vads-u-display--block">{cityStateZip}</span>
        </div>
      );
    }

    return (
      <div key={id} className="vads-u-margin-bottom--1p5">
        <input
          id={id}
          name="addressSelection"
          checked={selectedAddress === id}
          onChange={() => {
            handleValueChange(address, id);
          }}
          type="radio"
        />
        <label
          htmlFor={id}
          className="vads-u-margin-top--2 dd-privacy-hidden"
          data-dd-action-name="address"
        >
          {`${street} ${cityStateZip}`.trim()}
        </label>
      </div>
    );
  };

  // Render loading indicator while we fetch
  if (loading) return <Loader />;

  const hasData = apiData && apiData.length > 0;

  const deliveryPointValidation =
    hasData && apiData[0].addressMetaData.deliveryPointValidation;

  const confidenceScore =
    hasData && apiData[0].addressMetaData?.confidenceScore;

  const isHighConfidenceMatch =
    hasData &&
    confidenceScore >= 90 &&
    deliveryPointValidation === 'CONFIRMED' &&
    JSON.stringify(formatAddress(apiData[0].address)).toLowerCase() ===
      JSON.stringify(formatAddress(formData.address)).toLowerCase();

  if (isHighConfidenceMatch) {
    return (
      <>
        <va-alert
          class="vads-u-margin-y--3"
          status="success"
          role="alert"
          visible
        >
          <h4
            id="address-validation-alert-heading"
            slot="headline"
            className="vads-u-font-size--h3"
          >
            Address confirmed
          </h4>
          <p className="vads-u-margin-y--0">
            We’ve successfully validated the address you entered with the U.S.
            Postal Service.
          </p>
        </va-alert>
        <p className="vads-u-font-weight--bold vads-u-margin-top--3">
          Address you entered:
        </p>
        <div>{renderAddressOption(formData.address)}</div>
      </>
    );
  }

  const shouldShowSuggestions =
    hasData && deliveryPointValidation === 'CONFIRMED';

  const noRecommendationsAvailable =
    hasData && deliveryPointValidation !== 'CONFIRMED';

  return (
    <>
      <va-alert
        class="vads-u-margin-y--2 vads-u-padding-bottom--1"
        status="warning"
        role="alert"
        visible={shouldShowSuggestions || noRecommendationsAvailable}
      >
        <h4
          id="address-validation-alert-heading"
          slot="headline"
          className="vads-u-font-size--h3"
        >
          {shouldShowSuggestions
            ? `We can’t confirm the address you entered with the U.S. Postal
            Service`
            : `Confirm your address`}
        </h4>
        <p className="vads-u-margin-y--0">
          {shouldShowSuggestions
            ? 'Tell us which address you’d like to use.'
            : 'We can’t confirm the address you entered with the U.S. Postal Service. Confirm that you want us to use this address as you entered it. Or go back and edit it.'}
        </p>
      </va-alert>
      <div>
        <span className="vads-u-font-weight--bold">Address you entered:</span>
        {renderAddressOption(formData.address, shouldShowSuggestions)}
        {shouldShowSuggestions && (
          <span className="vads-u-font-weight--bold">
            Suggested {apiData.length > 1 ? 'addresses' : 'address'}:
          </span>
        )}
        {shouldShowSuggestions &&
          apiData.map((item, index) =>
            renderAddressOption(item.address, true, String(index)),
          )}
      </div>
    </>
  );
};

AddressValidationRadio.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func,
};

const mapStateToProps = state => ({
  formData: state.form?.data || {},
});

const mapDispatchToProps = {
  setFormData: setData,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddressValidationRadio);
